import TopNavbar from "@common/components/TopNavbar";
import { withPageAuthRequired } from "@common/middleware";
import { FC, useEffect } from "react";
import EventsList from "@modules/for-you/components/EventsList";
import NewsList from "@modules/for-you/components/NewsList";
import Reports from "@modules/for-you/components/Reports";
import ResourcesList from "@modules/for-you/components/ResourcesList";
import {
  DEFAULT_EVENTS_PAGE_SIZE,
  FOR_YOU_INFINITE_SCROLL_CONTAINER,
} from "@modules/for-you/constants";
import FilePreview from "@modules/docvault/components/FilePreview";
import { useStore as useDocVaultStore } from "@modules/docvault/state";
import { useDocVaultJwt } from "@modules/docvault/queries";
import { HiOutlineDownload, HiOutlinePrinter, HiX } from "react-icons/hi";
import { useDownload, usePrint } from "@modules/docvault/hooks";
import { BoxFile } from "@modules/box/types";
import { useReports } from "@modules/for-you/hooks";
import { useEvents, useNews } from "@modules/for-you/queries";
import LoadingIndicator from "@common/components/LoadingIndicator";

const ForYouPage: FC = () => {
  const { previewFile, previewTopNavFileName, resetPreview } =
    useDocVaultStore();
  const { data: token } = useDocVaultJwt();
  const { download } = useDownload({ file: previewFile || ({} as BoxFile) });
  const { print } = usePrint({ file: previewFile || ({} as BoxFile) });

  const { isLoading: isReportsLoading, isFetched: isReportsFetched } =
    useReports();
  const { isLoading: isEventsLoading } = useEvents({
    pageSize: DEFAULT_EVENTS_PAGE_SIZE,
  });

  // no need to wait for resources or news, they have dynamic height and extend out of view on load.
  // news and reports also have dynamic height but waiting for both to load prevents jarring page layout flickering/resizing:
  const isLoading = !isReportsFetched || isReportsLoading || isEventsLoading;

  useEffect(() => {
    // ensures preview doesn't persist if user navigates to or away from for-you page:
    resetPreview();
    return resetPreview;
  }, [resetPreview]);

  const renderLoading = () => {
    return <LoadingIndicator isVisible />;
  };
  const renderPreviewFile = (previewFile: BoxFile) => {
    return (
      <div className="relative flex flex-1 flex-col overflow-auto">
        <FilePreview accessToken={token?.accessToken} files={[previewFile]} />
      </div>
    );
  };

  const renderDashboard = () => {
    return (
      <div
        id={FOR_YOU_INFINITE_SCROLL_CONTAINER}
        className="overflow-auto sm:grid lg:auto-cols-min lg:grid-cols-12"
      >
        <div className="bg-green-40 px-10 py-6 lg:col-span-8">
          <Reports />
        </div>
        <div className="bg-green-40 px-10 lg:col-span-4 lg:pl-0">
          <EventsList />
        </div>
        <div className="bg-green-40 p-10 lg:order-4 lg:col-span-4 lg:pl-0 lg:backdrop-opacity-30">
          <NewsList />
        </div>
        <div className="bg-green-40 p-10 lg:order-3 lg:col-span-8 lg:pr-10 lg:backdrop-opacity-30">
          <ResourcesList />
        </div>
      </div>
    );
  };

  return (
    <div className="relative flex flex-1 flex-col text-purple-2-900">
      {previewFile && (
        <TopNavbar title={previewTopNavFileName as string}>
          {previewFile && (
            <div className="justify-right flex">
              <button className="mr-5" onClick={download}>
                <HiOutlineDownload size={24} />
              </button>
              <button className="mr-5" onClick={print}>
                <HiOutlinePrinter size={24} />
              </button>
              <button onClick={resetPreview}>
                <HiX size={24} />
              </button>
            </div>
          )}
        </TopNavbar>
      )}
      {isLoading
        ? renderLoading()
        : previewFile
        ? renderPreviewFile(previewFile)
        : renderDashboard()}
    </div>
  );
};

export default withPageAuthRequired(ForYouPage);
